import type { EditorDraft } from "./editor-types";

export type DbProfile = {
  id?: string;
  fullName: string;
  title?: string | null;
  photoUrl?: string | null;
  bio?: string | null;
  language?: string | null;
  subdomain: string;
  customDomain?: string | null;
  themeKey?: string | null;
  fontPairing?: string | null;
  backgroundStyle?: string | null;
  headerStyle?: string | null;
  animationLevel?: string | null;
  customCss?: string | null;
  colorPalette?: string | null;
  layoutDensity?: string | null;
  cornerRadius?: string | null;
  sectionArrangement?: string | null;
  typographyScale?: string | null;
  layoutOverrides?: unknown;
  customColors?: unknown;
  seoOverrides?: unknown;
  draftVersion: number;
  [key: string]: any;
};

export type DbSection = {
  id: string;
  pageId?: string;
  type: string;
  visible: boolean;
  displayOrder: number;
  content?: unknown;
  [key: string]: any;
};

export function normalizeServerToDraft(profile: DbProfile, sections: DbSection[]): EditorDraft {
  const profileData = {
    fullName: profile.fullName,
    title: profile.title,
    photoUrl: profile.photoUrl,
    bio: profile.bio,
    language: profile.language || "en",
    subdomain: profile.subdomain,
    customDomain: profile.customDomain,
    themeKey: profile.themeKey || "classic-academic",
    fontPairing: profile.fontPairing || "serif-classic",
    backgroundStyle: profile.backgroundStyle || "solid",
    headerStyle: profile.headerStyle || "standard",
    animationLevel: profile.animationLevel || "subtle",
    // @deprecated Inactive and non-rendered. Passed through for data preservation only.
    customCss: profile.customCss || "",
    colorPalette: profile.colorPalette || "ocean-blue",
    layoutDensity: profile.layoutDensity || "standard",
    cornerRadius: profile.cornerRadius || "subtle",
    sectionArrangement: profile.sectionArrangement || "linear",
    typographyScale: profile.typographyScale || "editorial",
    layoutOverrides: (profile.layoutOverrides as Record<string, unknown>) || {},
    customColors: (profile.customColors as { primary?: string; accent?: string; background?: string; foreground?: string } | null) || null,
    seoOverrides: (profile.seoOverrides as { ogTitle?: string; ogDescription?: string; ogImageUrl?: string }) || {},
  };

  const sectionData = sections.map((s) => ({
    id: s.id,
    clientKey: (s as any).clientKey || s.id,
    pageId: s.pageId || (s as any).page_id,
    type: s.type,
    visible: s.visible,
    displayOrder: s.displayOrder,
    content: (s.content as Record<string, unknown>) || {},
  }));

  return {
    profile: profileData,
    sections: sectionData,
    designPreset: profile.themeKey || "classic-academic",
    draftVersion: profile.draftVersion,
  };
}

/**
 * Merges the confirmed base EditorDraft with any active transient design preview overrides.
 * This guarantees that what the user sees in the editor/preview canvas is exactly what
 * is saved and published to Cloudflare KV.
 */
export function composeEffectiveDraft(
  draft: EditorDraft,
  transientOverride?: Partial<EditorDraft["profile"]> | null,
  transientSectionOverrides?: Record<string, any>
): EditorDraft {
  if (
    (!transientOverride || Object.keys(transientOverride).length === 0) &&
    (!transientSectionOverrides || Object.keys(transientSectionOverrides).length === 0)
  ) {
    return draft;
  }

  const baseProfile = draft.profile;

  // Deep-merge layoutOverrides if both exist
  let mergedLayoutOverrides = baseProfile.layoutOverrides;
  if (transientOverride && transientOverride.layoutOverrides && typeof transientOverride.layoutOverrides === "object") {
    const baseOverrides = (baseProfile.layoutOverrides as Record<string, any>) || {};
    const transOverrides = transientOverride.layoutOverrides as Record<string, any>;
    mergedLayoutOverrides = {
      ...baseOverrides,
      ...transOverrides,
      hero: {
        ...((baseOverrides.hero as Record<string, any>) || {}),
        ...((transOverrides.hero as Record<string, any>) || {}),
      },
      effects: {
        ...((baseOverrides.effects as Record<string, any>) || {}),
        ...((transOverrides.effects as Record<string, any>) || {}),
      } as any,
      sections: {
        ...(baseOverrides.sections || {}),
        ...(transOverrides.sections || {}),
      },
      sectionOverridesById: {
        ...(baseOverrides.sectionOverridesById || {}),
        ...(transOverrides.sectionOverridesById || {}),
      },
    };
  }

  const effectiveSections = draft.sections.map((section) => {
    const override = transientSectionOverrides?.[section.id];
    if (override) {
      return {
        ...section,
        content: {
          ...((section.content as object) || {}),
          ...override,
        },
      };
    }
    return section;
  });

  return {
    ...draft,
    profile: {
      ...baseProfile,
      ...transientOverride,
      layoutOverrides: mergedLayoutOverrides,
      customColors:
        transientOverride?.customColors !== undefined
          ? transientOverride.customColors
          : baseProfile.customColors,
      seoOverrides:
        transientOverride?.seoOverrides !== undefined
          ? transientOverride.seoOverrides
          : baseProfile.seoOverrides,
    },
    sections: effectiveSections,
  };
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function normalizeUrl(url?: string | null): string | undefined {
  if (!url || typeof url !== "string") return undefined;
  const trimmed = url.trim();
  if (!trimmed) return undefined;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^[\w-]+(\.[\w-]+)+/.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

function isValidHttpUrl(url?: string | null): boolean {
  if (!url || typeof url !== "string") return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Normalizes an EditorDraft for safe network transmission to saveBulkDraft.
 * 
 * Rules:
 * 1. PURE: Never mutates the original draft or React state.
 * 2. NO FAKE DATA: Never invents placeholder strings ("General", "COURSE 101", fake URLs).
 * 3. LEGACY API SAFE: Brand-new uncommitted empty items (e.g. empty categoryName, empty course title)
 *    are omitted from the network snapshot until the user enters content.
 * 4. COMPLETE COVERAGE: Normalizes all section types, groups, items, and profile fields.
 */
export function normalizeDraftForSave(draft: EditorDraft): EditorDraft {
  const baseProfile = draft.profile;

  const normalizedProfile = {
    ...baseProfile,
    fullName: baseProfile.fullName?.trim() || "Scholar",
    title: baseProfile.title?.trim() || null,
    bio: baseProfile.bio?.trim() || null,
    photoUrl: baseProfile.photoUrl?.trim() || null,
    language: baseProfile.language || "en",
    subdomain: baseProfile.subdomain,
    customDomain: baseProfile.customDomain || null,
    themeKey: baseProfile.themeKey || "classic-academic",
    fontPairing: baseProfile.fontPairing || "serif-classic",
    backgroundStyle: baseProfile.backgroundStyle || "solid",
    headerStyle: baseProfile.headerStyle || "standard",
    animationLevel: baseProfile.animationLevel || "subtle",
    // customCss is explicitly undefined to satisfy strict update validation rules and overwrite the spread baseProfile
    customCss: undefined,
    colorPalette: baseProfile.colorPalette || "ocean-blue",
    layoutDensity: baseProfile.layoutDensity || "standard",
    cornerRadius: baseProfile.cornerRadius || "subtle",
    sectionArrangement: baseProfile.sectionArrangement || "linear",
    typographyScale: baseProfile.typographyScale || "editorial",
    layoutOverrides: (() => {
      const overrides = { ...((baseProfile.layoutOverrides as Record<string, unknown>) || {}) };
      delete overrides._deprecated_proCustomCss;
      return overrides;
    })(),
    customColors: baseProfile.customColors || null,
    seoOverrides: (baseProfile.seoOverrides as Record<string, unknown>) || {},
  };

  const normalizedSections = (draft.sections || []).map((s) => {
    const rawContent = (s.content as Record<string, any>) || {};
    let cleanContent = { ...rawContent };

    // Explicit, section-specific mapping table for variant -> displayOptions
    // Lossless mapping only: maps legacy values ONLY if they exactly match a valid presentation ID in the new registry.
    if (typeof cleanContent.variant === "string" && !cleanContent.displayOptions) {
      const explicitVariantMappings: Record<string, Record<string, string>> = {
        about: { "standard": "standard", "compact": "compact", "split": "split" },
        publications: { "standard": "standard", "cards": "cards" }, // 'compact' is NOT mapped to 'compact_bibliography' to avoid data mutation
        research: { "standard": "standard", "compact": "compact", "cards": "cards" }, // 'visual' not mapped
        experience: { "timeline": "timeline", "cards": "cards", "minimal": "minimal" }, // 'standard' and 'compact' not mapped
        contact: { "standard": "standard", "split": "split", "minimal": "minimal" },
        teaching: { "standard": "standard", "compact": "compact", "cards": "cards" },
        downloads: { "standard": "standard", "grid": "grid", "list": "list" },
        projects: { "grid": "grid", "list": "list", "featured": "featured" },
        skills: { "grid": "grid" }, // 'tags' and 'progress' are not valid presentations in registry if they don't exist
        media: { "gallery": "gallery", "carousel": "carousel", "featured": "featured" },
      };

      const sectionMap = explicitVariantMappings[s.type as string];
      if (sectionMap) {
        const mappedPresentation = sectionMap[cleanContent.variant];
        if (mappedPresentation) {
          cleanContent.displayOptions = {
            presentation: mappedPresentation,
          };
          delete cleanContent.variant;
        }
      }
    }

    switch (s.type) {
      case "skills": {
        const rawGroups = Array.isArray(rawContent.groups) ? rawContent.groups : [];
        // Only include groups with a non-empty categoryName or non-empty skills
        const cleanGroups = rawGroups
          .filter((g: any) => (
            (typeof g?.categoryName === "string" && g.categoryName.trim().length > 0) ||
            (Array.isArray(g?.skills) && g.skills.some((sk: any) => sk?.name?.trim?.()))
          ))
          .map((g: any) => ({
            ...g,
            categoryName: g.categoryName?.trim() || "",
            skills: (g.skills || [])
              .filter((sk: any) => typeof sk?.name === "string" && sk.name.trim().length > 0)
              .map((sk: any) => ({ ...sk, name: sk.name.trim() })),
          }));

        cleanContent = {
          ...rawContent,
          heading: rawContent.heading?.trim() || "Skills & Methodologies",
          displayStyle: rawContent.displayStyle || "categorized_cards",
          groups: cleanGroups,
        };
        break;
      }

      case "teaching": {
        const rawItems = Array.isArray(rawContent.items) ? rawContent.items : [];
        // Only include items where at least courseCode, courseTitle, institution, or description has text
        const cleanItems = rawItems
          .filter((item: any) => (
            item?.courseCode?.trim?.() ||
            item?.courseTitle?.trim?.() ||
            item?.institution?.trim?.() ||
            item?.description?.trim?.()
          ))
          .map((item: any) => {
            const courseUrlNorm = normalizeUrl(item.courseUrl);
            const courseUrl = courseUrlNorm && isValidHttpUrl(courseUrlNorm) ? courseUrlNorm : undefined;
            const syllabusUrlNorm = normalizeUrl(item.syllabusUrl);
            const syllabusUrl = syllabusUrlNorm && isValidHttpUrl(syllabusUrlNorm) ? syllabusUrlNorm : undefined;

            return {
              ...item,
              courseCode: item.courseCode?.trim() || "",
              courseTitle: item.courseTitle?.trim() || "",
              institution: item.institution?.trim() || undefined,
              term: item.term?.trim() || undefined,
              description: item.description?.trim() || undefined,
              courseUrl,
              syllabusUrl,
            };
          });

        cleanContent = {
          ...rawContent,
          heading: rawContent.heading?.trim() || "Teaching & Courses",
          displayStyle: rawContent.displayStyle || "grouped_by_level",
          items: cleanItems,
        };
        break;
      }

      case "projects": {
        const rawItems = Array.isArray(rawContent.items) ? rawContent.items : [];
        // Only include items where at least title, fundingAgency, or description has text
        const cleanItems = rawItems
          .filter((item: any) => (
            item?.title?.trim?.() ||
            item?.fundingAgency?.trim?.() ||
            item?.description?.trim?.()
          ))
          .map((item: any) => {
            const projectUrlNorm = normalizeUrl(item.projectUrl);
            const projectUrl = projectUrlNorm && isValidHttpUrl(projectUrlNorm) ? projectUrlNorm : undefined;
            const imageUrlNorm = normalizeUrl(item.imageUrl);
            const imageUrl = imageUrlNorm && isValidHttpUrl(imageUrlNorm) ? imageUrlNorm : undefined;

            return {
              ...item,
              title: item.title?.trim() || "",
              fundingAgency: item.fundingAgency?.trim() || undefined,
              grantNumber: item.grantNumber?.trim() || undefined,
              amount: item.amount?.trim() || undefined,
              startDate: item.startDate?.trim() || undefined,
              endDate: item.endDate?.trim() || undefined,
              collaborators: item.collaborators?.trim() || undefined,
              description: item.description?.trim() || undefined,
              projectUrl,
              imageUrl,
            };
          });

        cleanContent = {
          ...rawContent,
          heading: rawContent.heading?.trim() || "Projects & Grants",
          displayStyle: rawContent.displayStyle || "cards",
          items: cleanItems,
        };
        break;
      }

      case "contact": {
        const rawLinks = Array.isArray(rawContent.socialLinks) ? rawContent.socialLinks : [];
        // Only include social links with valid/non-empty URL
        const cleanLinks = rawLinks
          .map((link: any) => {
            const rawUrl = typeof link?.url === "string" ? link.url.trim() : "";
            const normalizedUrl = normalizeUrl(rawUrl);
            return {
              ...link,
              url: normalizedUrl || "",
              label: typeof link?.label === "string" ? link.label.trim() : undefined,
            };
          })
          .filter((link: any) => link.url.length > 0 && isValidHttpUrl(link.url));

        const emailCandidate = typeof rawContent.email === "string" ? rawContent.email.trim() : "";
        const cleanEmail = emailCandidate && isValidEmail(emailCandidate) ? emailCandidate : undefined;

        const websiteCandidate = normalizeUrl(rawContent.website);
        const cleanWebsite = websiteCandidate && isValidHttpUrl(websiteCandidate) ? websiteCandidate : undefined;

        cleanContent = {
          ...rawContent,
          heading: rawContent.heading?.trim() || "Contact & Profiles",
          email: cleanEmail,
          phone: rawContent.phone?.trim() || undefined,
          office: rawContent.office?.trim() || undefined,
          address: rawContent.address?.trim() || undefined,
          website: cleanWebsite,
          showContactForm: Boolean(rawContent.showContactForm),
          socialLinks: cleanLinks,
        };
        break;
      }

      case "about": {
        const rawHighlights = Array.isArray(rawContent.highlights) ? rawContent.highlights : [];
        // Only include highlights where either label or value has text
        const cleanHighlights = rawHighlights
          .filter((h: any) => (h?.label?.trim?.() || h?.value?.trim?.()))
          .map((h: any) => ({
            label: h.label?.trim() || "",
            value: h.value?.trim() || "",
          }));

        cleanContent = {
          ...rawContent,
          heading: rawContent.heading?.trim() || "About",
          body: rawContent.body || "",
          highlights: cleanHighlights,
        };
        break;
      }

      case "downloads": {
        const rawItems = Array.isArray(rawContent.items) ? rawContent.items : [];
        // Keep all items, sanitize title
        const cleanItems = rawItems.map((item: any) => ({
          ...item,
          title: item.title?.trim() || "",
        }));

        cleanContent = {
          ...rawContent,
          heading: rawContent.heading?.trim() || "Downloads & Resources",
          items: cleanItems,
        };
        break;
      }

      case "experience": {
        const rawItems = Array.isArray(rawContent.items) ? rawContent.items : [];
        // Only include items where organization and role are present
        const cleanItems = rawItems
          .filter((item: any) => item?.organization?.trim?.() && item?.role?.trim?.())
          .map((item: any) => ({
            ...item,
            organization: item.organization.trim(),
            role: item.role.trim(),
            startDate: item.startDate?.trim() || "",
          }));

        cleanContent = {
          ...rawContent,
          heading: rawContent.heading?.trim() || "Experience",
          items: cleanItems,
        };
        break;
      }

      case "publications": {
        const rawItems = Array.isArray(rawContent.items) ? rawContent.items : [];
        // Only include publications where title and authors are present
        const cleanItems = rawItems
          .filter((item: any) => item?.title?.trim?.() && item?.authors?.trim?.())
          .map((item: any) => ({
            ...item,
            title: item.title.trim(),
            authors: item.authors.trim(),
          }));

        cleanContent = {
          ...rawContent,
          heading: rawContent.heading?.trim() || "Publications",
          displayStyle: rawContent.displayStyle || "grouped_by_year",
          items: cleanItems,
        };
        break;
      }

      case "research": {
        const rawAreas = Array.isArray(rawContent.areas) ? rawContent.areas : [];
        const cleanAreas = rawAreas
          .filter((area: any) => area?.title?.trim?.())
          .map((area: any) => ({
            ...area,
            title: area.title.trim(),
          }));

        cleanContent = {
          ...rawContent,
          heading: rawContent.heading?.trim() || "Research Interests",
          summary: rawContent.summary?.trim() || undefined,
          areas: cleanAreas,
        };
        break;
      }

      case "media": {
        const rawItems = Array.isArray(rawContent.items) ? rawContent.items : [];
        const cleanItems = rawItems.map((item: any) => ({
          ...item,
          url: item.url?.trim() || "",
          alt: item.alt?.trim() || "",
          caption: item.caption?.trim() || "",
        }));

        cleanContent = {
          ...rawContent,
          heading: rawContent.heading?.trim() || "",
          description: rawContent.description?.trim() || "",
          items: cleanItems,
        };
        break;
      }

      default:
        break;
    }

    return {
      id: s.id,
      clientKey: (s as any).clientKey || s.id,
      pageId: (s as any).pageId,
      type: s.type,
      visible: Boolean(s.visible),
      displayOrder: Number(s.displayOrder ?? 0),
      content: cleanContent,
    };
  });

  return {
    ...draft,
    profile: normalizedProfile,
    sections: normalizedSections,
  };
}

/**
 * Identifies any pending/incomplete items in the local editor draft
 * that require user input before publication.
 */
export function getPendingDraftValidationErrors(draft: EditorDraft): string[] {
  const errors: string[] = [];

  for (const s of draft.sections || []) {
    const c = (s.content as Record<string, any>) || {};
    if (s.type === "skills") {
      (c.groups || []).forEach((g: any, i: number) => {
        if (!g.categoryName || !g.categoryName.trim()) {
          errors.push(`Skills → Group #${i + 1}: Category name is required`);
        }
      });
    } else if (s.type === "teaching") {
      (c.items || []).forEach((item: any, i: number) => {
        if (!item.courseTitle || !item.courseTitle.trim()) {
          errors.push(`Teaching → Course #${i + 1}: Course title is required`);
        }
      });
    } else if (s.type === "projects") {
      (c.items || []).forEach((item: any, i: number) => {
        if (!item.title || !item.title.trim()) {
          errors.push(`Projects → Project #${i + 1}: Title is required`);
        }
      });
    } else if (s.type === "contact") {
      (c.socialLinks || []).forEach((l: any, i: number) => {
        if (!l.url || !l.url.trim()) {
          errors.push(`Contact → Profile link #${i + 1}: URL is required`);
        }
      });
    }
  }

  return errors;
}
